import { useCallback, useEffect, useRef } from 'react';

/**
 * Syncs a native modal <dialog> with the `isOpen` state and provides the related event handlers.
 *
 * @param {Object}   options
 * @param {boolean}  options.isOpen         Whether the dialog should be shown.
 * @param {(element: HTMLDialogElement | null) => void} [options.onRefChange] Called whenever the ref changes.
 * @param {() => boolean} [options.preventClose] Returns true if Escape should not close the dialog.
 */
export const useModalDialog = ({ isOpen, onRefChange, preventClose }) => {
	const dialogRef = useRef(/** @type {HTMLDialogElement | null} */ (null));

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) {
			return;
		}

		if (isOpen && !dialog.open) {
			dialog.showModal();
		} else if (!isOpen && dialog.open) {
			dialog.close();
		}
	}, [isOpen]);

	const setDialogRef = useCallback(
		/** @param {HTMLDialogElement | null} element */
		(element) => {
			dialogRef.current = element;
			onRefChange?.(element);
		},
		[onRefChange],
	);

	const close = () => dialogRef.current?.close();

	// Clicks on the backdrop target the dialog element itself.
	/** @param {import('react').MouseEvent<HTMLDialogElement>} event */
	const handleBackdropClick = (event) => {
		if (event.target === dialogRef.current) {
			close();
		}
	};

	/** @param {import('react').SyntheticEvent<HTMLDialogElement>} event */
	const handleCancel = (event) => {
		if (preventClose?.()) {
			event.preventDefault();
		}
	};

	return { dialogRef, setDialogRef, close, handleBackdropClick, handleCancel };
};
